import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import { BUSINESS, ASSETS } from "@/data";

const SERVICE_OPTIONS = [
  "Custom Home Building",
  "Renovation / Remodel",
  "New Construction",
  "Design & Planning",
  "Other",
];

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <main data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title={<>LET'S BUILD<br />TOGETHER.</>}
        subtitle="Tell us about your project and we'll get back to you with a free, no-pressure quote."
        image={ASSETS.toolRack}
      />

      <section className="bg-cream" style={{ backgroundColor: "var(--l3-cream)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid lg:grid-cols-5 gap-12">
          {/* Info column */}
          <div className="lg:col-span-2">
            <p className="font-cond tracking-[0.3em] text-sm mb-3" style={{ color: "var(--l3-gold)" }}>GET IN TOUCH</p>
            <h2 className="font-display text-4xl mb-6" style={{ color: "var(--l3-wood-dark)" }}>WE'D LOVE TO HEAR FROM YOU</h2>
            <p className="text-[16px] leading-relaxed mb-8" style={{ color: "#5b4f3d" }}>
              With owners who bring over {BUSINESS.experienceYears} years of combined homebuilding
              experience, we're ready to bring your vision to life. Reach out and let's start the conversation.
            </p>

            <ul className="space-y-5">
              <li className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-sm shrink-0" style={{ backgroundColor: "var(--l3-wood-dark)" }}>
                  <Phone size={22} style={{ color: "var(--l3-gold)" }} />
                </div>
                <div>
                  <p className="font-cond tracking-widest text-xs" style={{ color: "var(--l3-gold)" }}>CALL US</p>
                  <a href={BUSINESS.phoneHref} className="text-lg hover:underline" style={{ color: "var(--l3-wood-dark)" }}>{BUSINESS.phone}</a>
                </div>
              </li>

              <li className="flex items-start gap-4" data-testid="contact-emails">
                <div className="w-12 h-12 flex items-center justify-center rounded-sm shrink-0" style={{ backgroundColor: "var(--l3-wood-dark)" }}>
                  <Mail size={22} style={{ color: "var(--l3-gold)" }} />
                </div>
                <div>
                  <p className="font-cond tracking-widest text-xs" style={{ color: "var(--l3-gold)" }}>EMAIL</p>
                  <div className="flex flex-col">
                    {BUSINESS.emails.map((em) => (
                      <a key={em} href={`mailto:${em}`} className="text-lg hover:underline" style={{ color: "var(--l3-wood-dark)" }}>{em}</a>
                    ))}
                  </div>
                </div>
              </li>

              {[
                { icon: MapPin, label: "Service Area", value: BUSINESS.serviceArea },
                { icon: Clock, label: "Hours", value: "Mon – Sat · 7:00 AM – 4:00 PM" },
              ].map((item) => (
                <li key={item.label} className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-sm shrink-0" style={{ backgroundColor: "var(--l3-wood-dark)" }}>
                    <item.icon size={22} style={{ color: "var(--l3-gold)" }} />
                  </div>
                  <div>
                    <p className="font-cond tracking-widest text-xs" style={{ color: "var(--l3-gold)" }}>{item.label.toUpperCase()}</p>
                    <p className="text-lg" style={{ color: "var(--l3-wood-dark)" }}>{item.value}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Form column */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-sm shadow-xl p-8 lg:p-10 border border-[#d8ccb2]">
              {submitted ? (
                <div data-testid="contact-success" className="text-center py-12">
                  <CheckCircle2 size={56} className="mx-auto mb-5" style={{ color: "var(--l3-gold)" }} />
                  <h3 className="font-display text-3xl mb-3" style={{ color: "var(--l3-wood-dark)" }}>THANK YOU!</h3>
                  <p className="text-[16px]" style={{ color: "#5b4f3d" }}>
                    Your message has been received. We'll be in touch shortly to discuss your project.
                  </p>
                  <button
                    data-testid="contact-reset-btn"
                    onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", service: "", message: "" }); }}
                    className="mt-7 font-cond tracking-widest px-8 py-3 btn-outline-gold rounded-sm"
                    style={{ color: "var(--l3-wood-dark)", borderColor: "var(--l3-gold)" }}
                  >
                    SEND ANOTHER
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Full Name" testid="contact-name" required value={form.name} onChange={update("name")} placeholder="John Smith" />
                    <Field label="Phone" testid="contact-phone" type="tel" value={form.phone} onChange={update("phone")} placeholder="(775) 000-0000" />
                  </div>
                  <Field label="Email" testid="contact-email" type="email" required value={form.email} onChange={update("email")} placeholder="you@email.com" />
                  <div>
                    <label className="block font-cond tracking-widest text-xs mb-2" style={{ color: "var(--l3-wood-dark)" }}>SERVICE NEEDED</label>
                    <select
                      data-testid="contact-service"
                      value={form.service}
                      onChange={update("service")}
                      className="w-full rounded-sm border px-4 py-3 outline-none focus:border-[var(--l3-gold)] transition-colors"
                      style={{ borderColor: "#cdbfa3", color: "var(--l3-wood-dark)" }}
                    >
                      <option value="">Select a service…</option>
                      {SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block font-cond tracking-widest text-xs mb-2" style={{ color: "var(--l3-wood-dark)" }}>PROJECT DETAILS</label>
                    <textarea
                      data-testid="contact-message"
                      required
                      rows={5}
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Tell us a bit about what you're planning…"
                      className="w-full rounded-sm border px-4 py-3 outline-none focus:border-[var(--l3-gold)] transition-colors resize-none"
                      style={{ borderColor: "#cdbfa3", color: "var(--l3-wood-dark)" }}
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="contact-submit-btn"
                    className="w-full inline-flex items-center justify-center gap-2 font-cond tracking-widest px-8 py-4 btn-gold rounded-sm"
                  >
                    SEND MESSAGE <Send size={18} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function Field({ label, testid, type = "text", value, onChange, placeholder, required }) {
  return (
    <div>
      <label className="block font-cond tracking-widest text-xs mb-2" style={{ color: "var(--l3-wood-dark)" }}>
        {label.toUpperCase()}{required && <span style={{ color: "var(--l3-gold)" }}> *</span>}
      </label>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-sm border px-4 py-3 outline-none focus:border-[var(--l3-gold)] transition-colors"
        style={{ borderColor: "#cdbfa3", color: "var(--l3-wood-dark)" }}
      />
    </div>
  );
}
